import React, { useState, useMemo } from "react";
import Chart from "react-apexcharts";
import { FiBarChart2, FiPieChart, FiTrendingUp, FiActivity } from "react-icons/fi";
import { _t } from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";

const modernColors = [
  "#2563eb",
  "#059669",
  "#d97706",
  "#7c3aed",
  "#db2777",
  "#0284c7",
  "#ea580c",
  "#4f46e5",
  "#0d9488",
  "#e11d48",
];

// Professional heading formatter directly using configured API heading
const cleanHeadingTitle = (rawHeading = "", t, _t) => {
  if (!rawHeading) return _t(t("Performance Analytics"));
  return _t(t(rawHeading));
};

const WaiterChartWidgetV2 = ({ data, heading, defaultView = "bar" }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState(defaultView); // 'bar' | 'hbar' | 'area' | 'donut'

  const formattedTitle = useMemo(() => cleanHeadingTitle(heading, t, _t), [heading, t]);

  const isCurrencyChart = useMemo(() => {
    if (!heading) return false;
    const h = heading.toLowerCase();
    return h.includes("revenue") || h.includes("sales") || h.includes("piechart") || h.includes("amount") || h.includes("share");
  }, [heading]);

  // Parse incoming data cleanly
  const { categories, values, totals, totalSum } = useMemo(() => {
    let items = [];
    let totalItems = [];

    if (data) {
      if (Array.isArray(data)) {
        items = data;
      } else if (data.bar && Array.isArray(data.bar)) {
        items = data.bar;
      } else if (data.donut && Array.isArray(data.donut)) {
        items = data.donut;
      } else if (typeof data.bar === "object") {
        items = Object.values(data.bar);
      }

      if (data.total && Array.isArray(data.total)) {
        totalItems = data.total;
      }
    }

    const cats = items.map(
      (item) =>
        item.name ||
        item.waiter_name ||
        item.user_name ||
        item.option_name ||
        item.label ||
        "Staff"
    );

    const vals = items.map((item) => {
      const raw = parseFloat(
        item.value || item.option_value || item.total_sales || item.sales || item.amount || 0
      );
      return isNaN(raw) ? 0 : parseFloat(raw.toFixed(2));
    });

    const sum = vals.reduce((acc, curr) => acc + curr, 0);

    return { categories: cats, values: vals, totals: totalItems, totalSum: sum };
  }, [data]);

  // Generate ApexChart Config
  const chartConfig = useMemo(() => {
    const isDonut = viewMode === "donut" || viewMode === "pie";
    const isPie = viewMode === "pie";
    const isHorizontal = viewMode === "hbar";
    const isArea = viewMode === "area";

    if (isDonut) {
      return {
        type: isPie ? "pie" : "donut",
        series: values,
        options: {
          chart: {
            type: isPie ? "pie" : "donut",
            width: "100%",
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            animations: { enabled: true, easing: "easeinout", speed: 500 },
            redrawOnParentResize: true,
            redrawOnWindowResize: true,
          },
          labels: categories,
          colors: modernColors,
          fill: {
            type: "solid",
            opacity: 1,
          },
          stroke: { width: 2, colors: ["#ffffff"] },
          dataLabels: { enabled: false },
          legend: {
            show: true,
            position: "bottom",
            horizontalAlign: "center",
            fontSize: "12px",
            fontWeight: 500,
            labels: { colors: "#475569" },
            markers: { width: 8, height: 8, radius: 4 },
            itemMargin: { horizontal: 10, vertical: 4 },
          },
          plotOptions: {
            pie: {
              expandOnClick: true,
              donut: {
                size: isPie ? "0%" : "76%",
                labels: {
                  show: !isPie,
                  name: { show: true, fontSize: "12px", fontWeight: 600, color: "#64748b" },
                  value: {
                    show: true,
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#0f172a",
                    formatter: (val) =>
                      parseFloat(val) >= 1000
                        ? `${(parseFloat(val) / 1000).toFixed(1)}k`
                        : parseFloat(val).toLocaleString(),
                  },
                  total: {
                    show: true,
                    label: _t(t("Total")),
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#64748b",
                    formatter: () =>
                      totalSum >= 1000 ? `${(totalSum / 1000).toFixed(1)}k` : totalSum.toLocaleString(),
                  },
                },
              },
            },
          },
          tooltip: {
            theme: "light",
            style: { fontSize: "12px" },
            y: {
              formatter: (val) => {
                const pct = totalSum > 0 ? ((val / totalSum) * 100).toFixed(1) : 0;
                return `${val.toLocaleString()} (${pct}%)`;
              },
            },
          },
        },
      };
    }

    const len = categories.length;
    let calculatedColumnWidth = "80%";
    if (len === 1) calculatedColumnWidth = "38%";
    else if (len === 2) calculatedColumnWidth = "68%";
    else if (len === 3) calculatedColumnWidth = "74%";
    else if (len === 4) calculatedColumnWidth = "78%";

    // Bar / HBar / Area config
    return {
      type: isArea ? "area" : "bar",
      series: [
        {
          name: formattedTitle,
          data: values,
        },
      ],
      options: {
        chart: {
          type: isArea ? "area" : "bar",
          width: "100%",
          toolbar: { show: false },
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          animations: { enabled: true, easing: "easeinout", speed: 500 },
          redrawOnParentResize: true,
          redrawOnWindowResize: true,
        },
        plotOptions: {
          bar: {
            horizontal: isHorizontal,
            columnWidth: calculatedColumnWidth,
            barHeight: "75%",
            borderRadius: 8,
            borderRadiusApplication: "end",
            distributed: !isArea,
            dataLabels: { position: isHorizontal ? "right" : "top" },
          },
        },
        colors: modernColors,
        stroke: isArea ? { curve: "smooth", width: 3, colors: ["#2563eb"] } : { width: 0 },
        fill: {
          type: "solid",
          opacity: isArea ? 0.35 : 1,
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        grid: {
          borderColor: "#f1f5f9",
          strokeDashArray: 4,
          padding: {
            top: 5,
            right: 10,
            bottom: 10,
            left: 10,
          },
          xaxis: { lines: { show: isHorizontal } },
          yaxis: { lines: { show: !isHorizontal } },
        },
        xaxis: {
          categories: categories,
          labels: {
            style: { colors: "#64748b", fontSize: "11px", fontWeight: 600 },
            trim: false,
            rotate: -45,
            rotateAlways: true,
            hideOverlappingLabels: false,
            maxHeight: 120,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          decimalsInFloat: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: "#64748b", fontSize: "11.5px", fontWeight: 600 },
            formatter: (val) => {
              if (val % 1 !== 0) return "";
              if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
              return Math.round(val);
            },
          },
        },
        tooltip: {
          theme: "light",
          style: { fontSize: "12px" },
          y: {
            formatter: (val) =>
              typeof val === "number"
                ? isCurrencyChart
                  ? `Rs ${val.toLocaleString()}`
                  : val.toLocaleString()
                : val,
          },
        },
      },
    };
  }, [viewMode, categories, values, totalSum, formattedTitle, isCurrencyChart, t]);

  const iconThemeClass = useMemo(() => {
    if (viewMode === "donut") return "amber";
    if (viewMode === "area") return "indigo";
    return "blue";
  }, [viewMode]);

  return (
    <div className="waiter-v2-chart-card">
      {/* Card Header */}
      <div className="waiter-v2-card-header">
        <div className="d-flex align-items-center gap-2">
          <div className={`waiter-v2-icon-box ${iconThemeClass}`}>
            {viewMode === "donut" ? (
              <FiPieChart size={16} />
            ) : viewMode === "area" ? (
              <FiActivity size={16} />
            ) : (
              <FiBarChart2 size={16} />
            )}
          </div>
          <div>
            <h6 className="waiter-v2-chart-title mb-0">{formattedTitle}</h6>
            <span className="waiter-v2-chart-sub">
              {categories.length} {_t(t("Staff Items Recorded"))}
            </span>
          </div>
        </div>

        {/* Multi-Visual Option Selector Toolbar */}
        <div className="waiter-v2-toggle-group">
          {/* 1. Vertical Column Bar */}
          <button
            type="button"
            className={`waiter-v2-toggle-btn ${viewMode === "bar" ? "active" : ""}`}
            onClick={() => setViewMode("bar")}
            title={_t(t("Column Bar Chart"))}
          >
            <FiBarChart2 size={14} />
          </button>

          {/* 2. Horizontal Progress Track Bar (Reference Style) */}
          <button
            type="button"
            className={`waiter-v2-toggle-btn ${viewMode === "hbar" ? "active" : ""}`}
            onClick={() => setViewMode("hbar")}
            title={_t(t("Horizontal Progress Bar"))}
          >
            <FiBarChart2 size={14} style={{ transform: "rotate(90deg)" }} />
          </button>

          {/* 3. Donut Chart */}
          <button
            type="button"
            className={`waiter-v2-toggle-btn ${viewMode === "donut" ? "active" : ""}`}
            onClick={() => setViewMode("donut")}
            title={_t(t("Donut Chart"))}
          >
            <FiPieChart size={14} />
          </button>

          {/* 4. Solid Pie Chart */}
          <button
            type="button"
            className={`waiter-v2-toggle-btn ${viewMode === "pie" ? "active" : ""}`}
            onClick={() => setViewMode("pie")}
            title={_t(t("Solid Pie Chart"))}
          >
            <FiPieChart size={14} style={{ opacity: 0.6 }} />
          </button>

          {/* 5. Smooth Area Line */}
          <button
            type="button"
            className={`waiter-v2-toggle-btn ${viewMode === "area" ? "active" : ""}`}
            onClick={() => setViewMode("area")}
            title={_t(t("Area Line Curve"))}
          >
            <FiActivity size={14} />
          </button>
        </div>
      </div>

      {/* Summary Totals Chips (if available) */}
      {totals && totals.length > 0 && (
        <div className="waiter-v2-totals-bar mb-3">
          {totals.map((tot, idx) => (
            <div key={idx} className="waiter-v2-total-chip">
              <span className="waiter-v2-chip-label">{tot.name}:</span>
              <strong className="waiter-v2-chip-val">{tot.formated_value || tot.value}</strong>
            </div>
          ))}
        </div>
      )}

      {/* Chart Canvas */}
      <div className="waiter-v2-chart-container w-100">
        {categories.length > 0 ? (
          viewMode === "hbar" ? (
            <div className="waiter-v2-progress-track-wrapper w-100 py-2 px-1">
              {categories.map((cat, idx) => {
                const val = values[idx] || 0;
                const maxVal = Math.max(...values, 1);
                const pct = Math.min(100, Math.max(10, (val / maxVal) * 100));
                const barColor = modernColors[idx % modernColors.length];

                return (
                  <div key={idx} className="waiter-v2-progress-row mb-3">
                    <div className="waiter-v2-progress-name">{cat}</div>
                    <div className="waiter-v2-progress-track flex-grow-1">
                      <div
                        className="waiter-v2-progress-fill d-flex align-items-center justify-content-end px-2"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: barColor,
                        }}
                      >
                        <span className="waiter-v2-progress-val">
                          {isCurrencyChart
                            ? val >= 1000 ? `Rs ${val.toLocaleString()}` : `Rs ${val}`
                            : val.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Bottom Category Legend Dots */}
              <div className="waiter-v2-progress-legend mt-3 pt-2">
                <div className="d-flex align-items-center flex-wrap" style={{ gap: "16px" }}>
                  {categories.map((cat, idx) => (
                    <div key={idx} className="waiter-v2-legend-item">
                      <span
                        className="waiter-v2-legend-dot"
                        style={{ backgroundColor: modernColors[idx % modernColors.length] }}
                      />
                      <span className="waiter-v2-legend-text">{cat}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="w-100 h-100">
              <Chart
                options={chartConfig.options}
                series={chartConfig.series}
                type={chartConfig.type}
                width="100%"
                // height={viewMode === "bar" ? 275 : 245}
                height={"100%"}
              />
            </div>
          )
        ) : (
          <div className="waiter-v2-empty-chart">
            <FiTrendingUp size={24} color="#94a3b8" />
            <span>{_t(t("No Chart Data Recorded"))}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaiterChartWidgetV2;